"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { X } from "lucide-react";
import { Drawer as DrawerPrimitive } from "vaul";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { formatDuration, type PodcastEpisode } from "@/lib/podcast";

import { EpisodeBadge } from "./episode-badge";

const DESKTOP_QUERY = "(min-width: 768px)";

function subscribeDesktop(onChange: () => void) {
  const mq = window.matchMedia(DESKTOP_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getDesktopSnapshot() {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

function getDesktopServerSnapshot() {
  return false;
}

function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    getDesktopSnapshot,
    getDesktopServerSnapshot,
  );
}

export function EpisodeDialog({
  episode,
  seasonNumber,
  onClose,
}: {
  episode: PodcastEpisode;
  seasonNumber: number;
  onClose: () => void;
}) {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <DialogPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-60 bg-black/60 backdrop-blur-[2px]" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed left-1/2 top-1/2 z-60 flex max-h-[88vh] w-[calc(100%-2.5rem)] max-w-[640px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl bg-white shadow-[0_40px_80px_-24px_rgba(0,0,0,0.35)] outline-none"
            style={{ animation: "episode-dialog-in 0.2s ease-out" }}
          >
            <DialogPrimitive.Title className="sr-only">
              {episode.title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close"
              className="absolute right-4 top-4 z-20 flex size-9 items-center justify-center rounded-full bg-black/5 text-black/60 transition-colors hover:bg-black/10 hover:text-black"
            >
              <X className="size-4" strokeWidth={2} />
            </DialogPrimitive.Close>
            <EpisodeDialogBody
              episode={episode}
              seasonNumber={seasonNumber}
              variant="dialog"
            />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
        <style jsx global>{`
          @keyframes episode-dialog-in {
            from { opacity: 0; transform: scale(0.96) translateY(8px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>
      </DialogPrimitive.Root>
    );
  }

  return (
    <DrawerPrimitive.Root open onOpenChange={(v) => !v && onClose()}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]" />
        <DrawerPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto flex max-h-[92dvh] max-w-md flex-col rounded-t-[24px] bg-white text-black shadow-[0_-20px_40px_-12px_rgba(0,0,0,0.25)] outline-none">
          <DrawerPrimitive.Title className="sr-only">
            {episode.title}
          </DrawerPrimitive.Title>
          <DrawerPrimitive.Description className="sr-only">
            Full episode notes
          </DrawerPrimitive.Description>
          <div className="mx-auto mt-3 h-1.5 w-10 shrink-0 rounded-full bg-black/15" />
          <EpisodeDialogBody
            episode={episode}
            seasonNumber={seasonNumber}
            variant="drawer"
          />
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

function EpisodeDialogBody({
  episode,
  seasonNumber,
  variant,
}: {
  episode: PodcastEpisode;
  seasonNumber: number;
  variant: "dialog" | "drawer";
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () =>
      setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 8);
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [episode]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className={`scrollbar-none flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-6 sm:p-8 ${
          variant === "drawer"
            ? "pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
            : ""
        }`}
      >
      <div className="flex items-center gap-3">
        <EpisodeBadge
          seasonNumber={seasonNumber}
          episodeNumber={episode.number}
        />
        <div className="text-[12px] font-semibold tracking-[0.04em] uppercase text-black/55">
          {episode.number != null && (
            <span className="text-purple-03">Episode {episode.number}</span>
          )}
          {episode.formattedDate && (
            <>
              {episode.number != null && <span className="mx-2 text-black/30">·</span>}
              <span>{episode.formattedDate}</span>
            </>
          )}
          {episode.duration > 0 && (
            <>
              <span className="mx-2 text-black/30">·</span>
              <span>{formatDuration(episode.duration)}</span>
            </>
          )}
        </div>
      </div>

      <h2
        id="episode-dialog-title"
        className="text-[clamp(1.5rem,2.5vw,1.875rem)] leading-tight font-bold text-purple-05"
      >
        {episode.title}
      </h2>

      {episode.description && (
        <p className="text-body leading-[1.65] text-black/70 whitespace-pre-line">
          {episode.description}
        </p>
      )}
      </div>
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-white to-transparent transition-opacity duration-200 ${
          atBottom ? "opacity-0" : "opacity-100"
        }`}
      />
    </div>
  );
}
